import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Messages } from 'primereact/messages';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';

import UserAccountService from '../../services/userAccountService';
import { AppCard, AppPage } from '../../components/general';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { UserAccountDto } from '../../models/user-account';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';

const UserAccounts = () => {
  const errors = useRef<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userAccounts, setUserAccounts] = useState<UserAccountDto[]>([]);
  const [search, setSearch] = useState<string>('');

  const navigate = useNavigate();

  const { user, isLoading: authIsLoading } = useAuth();

  const userAccountService = UserAccountService.getInstance();

  useEffect(() => {
    getUserAccounts();
  }, []);

  const getUserAccounts = (): void => {
    setIsLoading(true);
    userAccountService
      .getAll()
      .then((accounts) => {
        setUserAccounts(accounts);
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsLoading(false));
  };

  const showError = (error: string) => {
    errors.current.show({ severity: 'error', summary: 'Error:', detail: error, life: 10000 });
  };

  const onAdd = () => {
    navigate(`/user-accounts/new`);
  };

  const onEdit = (cognitoId: string) => {
    navigate(`/user-accounts/${cognitoId}`);
  };

  const onDelete = (account: UserAccountDto) => {
    confirmDialog({
      message: `Are you sure you want to delete this user account: ${account.id}?`,
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        setIsLoading(true);
        userAccountService
          .delete(account.id!)
          .then(() => getUserAccounts())
          .catch((error) => showError(error.message))
          .finally(() => setIsLoading(false));
      },
    });
  };

  const actionBodyTemplate = (account: UserAccountDto) => {
    return (
      <div className="flex">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          onClick={() => onEdit(account.id!)}
          tooltip="Edit User Account"
          tooltipOptions={{ position: 'left' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          onClick={() => onDelete(account)}
          tooltip="Delete User Account"
          tooltipOptions={{ position: 'left' }}
          severity="danger"
        />
      </div>
    );
  };

  const tableDef: TableDefinition<UserAccountDto> = {
    columns: [
      {
        field: 'email',
        header: 'Email',
        dataType: 'text',
      },
      {
        field: 'firstName',
        header: 'First Name',
        dataType: 'text' as const,
      },
      {
        field: 'lastName',
        header: 'Last Name',
        dataType: 'text' as const,
      },
      {
        field: 'roles',
        header: 'Roles',
        body: (account: UserAccountDto) => {
          return account.roles?.map((v) => <div key={v}>{v}</div>);
        },
        dataType: 'text' as const,
      },
      {
        header: 'Actions',
        body: actionBodyTemplate,
        dataType: 'none' as const,
        sortable: false,
        filter: false,
      },
    ],
  };

  return (
    <AppPage title="User Accounts" breadcrumbs="Admin - User Accounts">
      <AppCard>
        {authIsLoading || !user ? (
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading User Accounts...</span>
          </div>
        ) : (
          <>
            <Messages ref={errors} />
            <ConfirmDialog />
            <DataTable
              loading={isLoading}
              header={<DataTableSearchCreateHeader onCreate={onAdd} onSearchChange={setSearch} />}
              tableDef={tableDef}
              data={userAccounts}
              globalFilterValue={search}
            />
          </>
        )}
      </AppCard>
    </AppPage>
  );
};

export default UserAccounts;
